import {useLocation} from 'react-router-dom';

import {
  DashboardActive,
  DashboardInactive,
  DocumentsActive,
  DocumentsInactive,
  InvoicesActive,
  InvoicesInactive,
  OperationsActive,
  OperationsInactive,
  ReportsActive,
  ReportsInactive,
  ServicesActive,
  ServicesInactive,
  UsersActive,
  UsersInactive,
} from '@icons/index';
import {PlaylistAddCheckCircle, PlaylistAddCheckCircleOutlined} from '@mui/icons-material';
import I18n from '@utils/i18n.utils';

export enum AdminRoutes {
  DASHBOARD = 'dashboard',
  CLIENTS = 'clients',
  SUPPLIERS = 'suppliers',
  OPERATIONS = 'operations',
  INVOICES = 'invoices',
  REPORTS = 'reports',
  DOCUMENTS = 'documents',
  SETTINGS = 'settings',
  AUDITS = 'audits',
}

export namespace AdminRoutes {
  export const routes = () => [
    AdminRoutes.DASHBOARD,
    AdminRoutes.CLIENTS,
    AdminRoutes.SUPPLIERS,
    AdminRoutes.OPERATIONS,
    AdminRoutes.INVOICES,
    AdminRoutes.REPORTS,
    AdminRoutes.DOCUMENTS,
    AdminRoutes.AUDITS,
  ];

  export const label = (route: AdminRoutes): string => {
    switch (route) {
      case AdminRoutes.DASHBOARD:
        return I18n.t('routes.dashboard');
      case AdminRoutes.CLIENTS:
        return I18n.t('routes.clients');
      case AdminRoutes.SUPPLIERS:
        return I18n.t('routes.suppliers');
      case AdminRoutes.OPERATIONS:
        return I18n.t('routes.operations');
      case AdminRoutes.INVOICES:
        return I18n.t('routes.invoices');
      case AdminRoutes.REPORTS:
        return I18n.t('routes.reports');
      case AdminRoutes.DOCUMENTS:
        return I18n.t('routes.documents');
      case AdminRoutes.AUDITS:
        return I18n.t('routes.audits.nav');
      default:
        return '';
    }
  };

  export const Icon = (route: AdminRoutes, isActive: boolean): JSX.Element | null => {
    switch (route) {
      case AdminRoutes.DASHBOARD:
        return isActive ? <DashboardActive/> : <DashboardInactive/>;
      case AdminRoutes.CLIENTS:
        return isActive ? <UsersActive/> : <UsersInactive/>;
      case AdminRoutes.SUPPLIERS:
        return isActive ? <ServicesActive/> : <ServicesInactive/>;
      case AdminRoutes.OPERATIONS:
        return isActive ? <OperationsActive/> : <OperationsInactive/>;
      case AdminRoutes.INVOICES:
        return isActive ? <InvoicesActive/> : <InvoicesInactive/>;
      case AdminRoutes.REPORTS:
        return isActive ? <ReportsActive/> : <ReportsInactive/>;
      case AdminRoutes.DOCUMENTS:
        return isActive ? <DocumentsActive/> : <DocumentsInactive/>;
      case AdminRoutes.AUDITS:
        return isActive ? <PlaylistAddCheckCircle color="primary"/> : <PlaylistAddCheckCircleOutlined/>;
      default:
        return null;
    }
  };
}

export enum DashboardRoutes {
  STATS = 'stats',
  FINANCE = 'finance',
  OPERATIONS = 'operations',
  RANKS = 'ranks',
}

export namespace DashboardRoutes {
  export const routes = () => [
    {label: I18n.t('routes.stats'), path: DashboardRoutes.STATS},
    // {label: I18n.t("routes.ranks"), path: DashboardRoutes.RANKS},
  ];

  export const statsRoutes = () => [DashboardRoutes.FINANCE, DashboardRoutes.OPERATIONS];
}

export enum ClientsRoutes {
  LIST = 'list',
  UNITS = 'units',
  SERVICES = 'services',
  CONTRACTS = 'contracts',
}

export namespace ClientsRoutes {
  export const routes = () => [
    {label: I18n.t('routes.clients'), path: ClientsRoutes.LIST},
    {label: I18n.t('routes.units'), path: ClientsRoutes.UNITS},
    {label: I18n.t('routes.contracts'), path: ClientsRoutes.CONTRACTS},
    {label: I18n.t('routes.clientsServices'), path: ClientsRoutes.SERVICES},
  ];
}

export enum ClientDetailsRoutes {
  ORGANISATION = 'organisation',
  CLIENT_UNITS = 'client-units',
  CONTRACTED_SERVICES = 'contracted-services',
  DOCUMENTS = 'documents',
  CONTRACTS = 'contracts',
  USERS = 'users',
}

export namespace ClientDetailsRoutes {
  export const routes = () => [
    {label: I18n.t('routes.organisation'), path: ClientDetailsRoutes.ORGANISATION},
    {label: I18n.t('routes.clientUnits'), path: ClientDetailsRoutes.CLIENT_UNITS},
    {label: I18n.t('routes.contracts'), path: ClientDetailsRoutes.CONTRACTS},
    {label: I18n.t('routes.contractedServices'), path: ClientDetailsRoutes.CONTRACTED_SERVICES},
    {label: I18n.t('routes.documents'), path: ClientDetailsRoutes.DOCUMENTS},
    {label: I18n.t('routes.users'), path: ClientDetailsRoutes.USERS},
  ];
}

export enum ClientUnitDetailsRoutes {
  CLIENT_UNIT = 'client-unit',
  CONTRACTED_SERVICES = 'contracted-services',
  CONTRACTS = 'contracts',
  USERS = 'users',
  DOCUMENTS = 'documents'
}

export namespace ClientUnitDetailsRoutes {
  export const routes = () => [
    {label: I18n.t('routes.clientUnits'), path: ClientUnitDetailsRoutes.CLIENT_UNIT},
    {label: I18n.t('routes.contracts'), path: ClientUnitDetailsRoutes.CONTRACTS},
    {label: I18n.t('routes.contractedServices'), path: ClientUnitDetailsRoutes.CONTRACTED_SERVICES},
    {label: I18n.t('routes.documents'), path: ClientUnitDetailsRoutes.DOCUMENTS},
    {label: I18n.t('routes.users'), path: ClientUnitDetailsRoutes.USERS},
  ];
}

export enum SupplierUnitDetailsRoutes {
  SUPPLIER_UNIT = 'supplier-unit',
  USERS = 'users',
  DOCUMENTS = 'documents'
}

export namespace SupplierUnitDetailsRoutes {
  export const routes = () => [
    {label: I18n.t('routes.supplierUnits'), path: SupplierUnitDetailsRoutes.SUPPLIER_UNIT},
    {label: I18n.t('routes.users'), path: SupplierUnitDetailsRoutes.USERS},
    {label: I18n.t('routes.documents'), path: ClientUnitDetailsRoutes.DOCUMENTS},
  ];
}

export enum SuppliersRoutes {
  LIST = 'list',
  UNITS = 'units',
  SERVICES = 'services',
}

export namespace SuppliersRoutes {
  export const routes = () => [
    {label: I18n.t('routes.suppliers'), path: SuppliersRoutes.LIST},
    {label: I18n.t('routes.units'), path: SuppliersRoutes.UNITS},
    {label: I18n.t('routes.suppliersServices'), path: SuppliersRoutes.SERVICES},
  ];
}

export enum SupplierDetailsRoutes {
  ORGANISATION = 'organisation',
  SUPPLIER_UNITS = 'supplier-units',
  CATALOG = 'catalog',
  CONTRACTED_SERVICES = 'contracted-services',
  DOCUMENTS = 'documents',
  USERS = 'users',
}

export namespace SupplierDetailsRoutes {
  export const routes = () => [
    {label: I18n.t('routes.organisation'), path: SupplierDetailsRoutes.ORGANISATION},
    {label: I18n.t('routes.supplierUnits'), path: SupplierDetailsRoutes.SUPPLIER_UNITS},
    {label: I18n.t('routes.catalog'), path: SupplierDetailsRoutes.CATALOG},
    {label: I18n.t('routes.contractedServices'), path: SupplierDetailsRoutes.CONTRACTED_SERVICES},
    {label: I18n.t('routes.documents'), path: SupplierDetailsRoutes.DOCUMENTS},
    {label: I18n.t('routes.users'), path: SupplierDetailsRoutes.USERS},
  ];
}

export enum SupplerUnitDetailsRoutes {
  SUPPLIER_UNITS = 'supplier-units',
  DOCUMENTS = 'documents',
}

export namespace SupplerUnitDetailsRoutes {
  export const routes = () => [
    {label: I18n.t('routes.supplierUnits'), path: SupplierDetailsRoutes.SUPPLIER_UNITS},
    {label: I18n.t('routes.documents'), path: SupplierDetailsRoutes.DOCUMENTS},
  ];
}

export enum OperationsRoutes {
  EXECUTIONS = 'executions',
  NONCOMPLIANCE = 'noncompliance',
}

export namespace OperationsRoutes {
  export const routes = () => [
    {label: I18n.t('routes.executions'), path: OperationsRoutes.EXECUTIONS},
    {label: I18n.t('routes.noncompliance'), path: OperationsRoutes.NONCOMPLIANCE},
  ];
}

export enum InvoicesRoutes {
  EXECUTIONS = 'executions',
  EXECUTIONS_NESSY = 'executions-nessy',
  PURCHASE_STATEMENT = 'purchase-statement',
  INVOICES_VALIDATION = 'invoices-validation',
  BILLS = 'bills',
}

export namespace InvoicesRoutes {
  export const routes = () => [
    {label: I18n.t('routes.executions'), path: InvoicesRoutes.EXECUTIONS},
    {label: I18n.t('routes.purchaseStatement'), path: InvoicesRoutes.PURCHASE_STATEMENT},
    {label: I18n.t('routes.invoicesValidation'), path: InvoicesRoutes.INVOICES_VALIDATION},
    {label: I18n.t('routes.invoices'), path: InvoicesRoutes.BILLS},
  ];
}

// export enum AuditsRoutes {
//   ALL = "all",
//   INPROCESS = "in-process",
//   DONE = "done",
//   TEMPLATES = "templates",
// }

// export namespace AuditsRoutes {
//   export const routes = () => [
//     { label: I18n.t("routes.all"), path: AuditsRoutes.ALL },
//     { label: I18n.t("routes.auditsInProcess"), path: AuditsRoutes.INPROCESS },
//     { label: I18n.t("routes.auditsDone"), path: AuditsRoutes.DONE },
//     { label: I18n.t("routes.auditsTemplates"), path: AuditsRoutes.TEMPLATES },
//   ]
// }

export enum SettingsRoutes {
  USERS = 'users',
  WASTE = 'waste',
  CONTAINERS = 'containers',
  PRICE_CATALOG = 'price-catalog',
  SERVICE_KINDS = 'service-kinds',
  HISTORIES = 'histories',
  HISTORY = 'history',
  ISSUE_KINS = 'issue-kinds',
  ACCOUNTING = 'accounting',
  INVOICING_PERIOD = 'invoicing-period',
  CHANGE_RATE = 'change-rate',
  INTERFACE = 'interface',
  ORGANISATIONS_CONFIG = 'organisations-config',
  INDEXATION = 'indexations',
}

export namespace SettingsRoutes {
  export const routes = () => [
    {label: I18n.t('routes.users'), path: SettingsRoutes.USERS},
    {label: I18n.t('routes.waste'), path: SettingsRoutes.WASTE},
    {label: I18n.t('routes.containers'), path: SettingsRoutes.CONTAINERS},
    {label: I18n.t('routes.serviceTypes'), path: SettingsRoutes.SERVICE_KINDS},
    {label: I18n.t('routes.indexations'), path: SettingsRoutes.INDEXATION},
    {label: I18n.t('routes.history'), path: SettingsRoutes.HISTORIES},
    {label: I18n.t('routes.issue-kinds'), path: SettingsRoutes.ISSUE_KINS},
    {label: I18n.t('routes.accounting'), path: SettingsRoutes.ACCOUNTING},
    {label: I18n.t('routes.invoicing-period'), path: SettingsRoutes.INVOICING_PERIOD},
    {label: I18n.t('routes.change-rate'), path: SettingsRoutes.CHANGE_RATE},
    {label: I18n.t('routes.interface'), path: SettingsRoutes.INTERFACE},
    {label: I18n.t('routes.organisations-config'), path: SettingsRoutes.ORGANISATIONS_CONFIG}
  ];
}

export enum AuditDetailsRoutes {
  PRE_AUDIT = 'pre-audit',
  AUDIT = 'audit',
  PRECO = 'preco',
  OFFER = 'offer',
  OFFER_FOLLOWUP = 'offer-follow-up',
  OFFER_FORMALIZATION = 'offer-formalization',
  OFFER_ACTIVATION = 'offer-activation'
}

export namespace AuditDetailsRoutes {
  export const routes = () => [
    {label: I18n.t('routes.audits.pre-audit'), path: AuditDetailsRoutes.PRE_AUDIT, disabled: false},
    {label: I18n.t('routes.audits.audit'), path: AuditDetailsRoutes.AUDIT, disabled: false},
    {label: I18n.t('routes.audits.preco'), path: AuditDetailsRoutes.PRECO, disabled: false},
    {label: I18n.t('routes.audits.offer'), path: AuditDetailsRoutes.OFFER, disabled: false},
    {label: I18n.t('routes.audits.offerFollowUp'), path: AuditDetailsRoutes.OFFER_FOLLOWUP, disabled: false},
    {label: I18n.t('routes.audits.offerFormalization'), path: AuditDetailsRoutes.OFFER_FORMALIZATION, disabled: false},
    {label: I18n.t('routes.audits.offerActivation'), path: AuditDetailsRoutes.OFFER_ACTIVATION, disabled: false},
  ];
}

export enum OfferFollowUpDetailsRoutes {
  DETAILS_PARTNER = 'details-partner',
  DETAILS_PARTNERSTATUS = 'details-partner-status',
  DETAILS_OFFER = 'details-offer',
  DETAILS_OFFERSTATUS = 'details-offer-status',
}


export const useRouteMatch = (patterns: readonly string[] = []) => {
  const {pathname} = useLocation();

  for (let i = 0; i < patterns.length; i += 1) {
    const pattern = patterns[i];

    const possibleMatch = pathname.split('/').some((p) => p === pattern);

    if (possibleMatch) {
      return pattern;
    }
  }

  return null;
};
